/*global chrome*/

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import { Context } from "../Store/Store";
import CheckUserPlanService from "../Services/CheckUserPlan";
import { refreshSession, shouldRefreshSession } from "../Services/AuthService";
import { clearAuthCookies, getTokenExp, setAuthCookies } from "../Scripts/authCookies";

import { constants } from "../Utils/constants";
import { GetPaymentHistory } from "../Services/BillingService";
import { cancellationReasons } from "../Components/Account/constant";
import StripePortalSessionService from "../Services/StripeService";
import toast from "react-hot-toast";

const GlobalContext = createContext({});

const GlobalProvider = ({ children }) => {
  const [state, dispatch] = useContext(Context);
  const { email } = state?.user?.data ?? {};

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user"));
  } catch (e) {
    localStorage.removeItem("user");
  }
  const savedPlan = user?.group;

  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  // False until the first check answers, so install prompts don't flash for users who have it.
  const [isExtensionCheckDone, setIsExtensionCheckDone] = useState(false);
  const [plan, setPlan] = useState({});
  const [isPayingUser, setIsPayingUser] = useState(false);
  const [stripePortalSessionUrl, setStripePortalSessionUrl] = useState("");

  const isPaymentFailed =
    cancellationReasons.includes(plan?.eai?.cancellation_reason) ||
    cancellationReasons.includes(plan?.pod?.cancellation_reason);

  const checkExtensionInstalled = useCallback(() => {
    if (typeof chrome !== "undefined" && chrome?.runtime) {
      chrome.runtime.sendMessage(
        constants.EXTENSION_ID,
        { type: "hello" },
        (response) => {
          if (response && response.installed) {
            setIsExtensionInstalled(true);
          } else {
            setIsExtensionInstalled(false);
          }
          setIsExtensionCheckDone(true);
        }
      );
    } else {
      setIsExtensionInstalled(false);
      setIsExtensionCheckDone(true);
    }
  }, []);

  const checkUserPlan = useCallback(() => {
    if (!email) return;
    CheckUserPlanService(email).then((res) => {
      // An error body has no plan fields; don't read that as an inactive account
      if (!res.ok) return;
      res.json().then((json) => {
        if ((json.Plan && savedPlan && json.Plan !== savedPlan) || json.is_active === false) {
          // user plan has changed => log out user
          const lastLogin = localStorage.getItem("lastLogin");
          localStorage.clear();
          if (lastLogin !== null) {
            localStorage.setItem("lastLogin", lastLogin);
          }
          clearAuthCookies();
          // clear state
          dispatch({
            type: "RESET_STATE",
          });
        }
      });
    });
  }, [email, savedPlan, dispatch]);

  const checkTokenExpiration = useCallback(() => {
    // token expired or about to, get new token
    if (shouldRefreshSession()) {
      refreshSession();
    }
  }, []);

  useEffect(() => {
    // Rewrite the extension cookies from the stored token so they last as long as it does.
    // Sessions from before the cookies matched the token's lifetime get upgraded here.
    const token = localStorage.getItem("token");
    const exp = token && getTokenExp(token);
    if (user && exp && exp > new Date()) {
      setAuthCookies(user, token);
    }
    // Only on load; refreshSession keeps the cookies current after that
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      if (!state?.user?.data?.email) return;
      try {
        const res = await GetPaymentHistory(state?.user?.data?.email);
        const data = await res.json();
        if (data?.plan.length === 0) {
          toast.error(
            "Error fetching payment history, please contact Admin for support"
          );
          return;
        }
        const plan = data?.plan;
        setIsPayingUser(data?.isPaying);
        setPlan({
          pod: plan.find((item) => item.planType === "Pod"),
          eai: plan.find((item) => item.planType === "KYNZO" || item.planType === "Kynzo" || item.planType === "EAI"),
        });
      } catch (error) {
        toast.error("Something went wrong, please try again later");
      }
    };
    fetchPaymentHistory();
  }, [state?.user?.data?.email]);

  useEffect(() => {
    const fetchStripePortalSession = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const result = await StripePortalSessionService();
        const stripePortalSession = await result.json();
        const url = stripePortalSession?.data?.url;
        if (url) {
          setStripePortalSessionUrl(url);
        }
      } catch (error) {
        console.error("Error fetching current plan:", error);
      }
    };
    fetchStripePortalSession();
  }, []);

  useEffect(() => {
    checkExtensionInstalled();
    checkTokenExpiration();

    const interval = setInterval(() => {
      checkUserPlan();
      checkTokenExpiration();
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkUserPlan, checkTokenExpiration, checkExtensionInstalled]);

  return (
    <GlobalContext.Provider
      value={{
        isExtensionInstalled,
        isExtensionCheckDone,
        checkExtensionInstalled,
        plan,
        isPaymentFailed,
        stripePortalSessionUrl,
        isPayingUser,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

const useGlobalContext = () => useContext(GlobalContext);

export { GlobalProvider, useGlobalContext };
